import { z } from "zod";

/**
 * Settings → Storage (1011_storage_tiering). Mirrors data-api/src/storage/policy.ts.
 * Command-center catalogue: ONE PostgreSQL, so months age out through
 * partition export + drop instead of a live-DB hot trim / SQLite compaction.
 */
export const STORAGE_ACTIONS = [
  "partition_maintenance",
  "analytics_shard_export",
  "kronos_cache_clear",
  "sim_log_cleanup",
  "db_compaction",
  "restored_range_expiry",
  "prune_inactive_configs",
  "decision_log_rewrite",
] as const;
export type StorageAction = (typeof STORAGE_ACTIONS)[number];

export const StorageSettings = z.object({
  ticks_floor_hours: z.number(),
  checks_floor_hours: z.number(),
  overrides_floor_days: z.number(),
  kronos_horizon_days: z.number(),
  kronos_horizon_cap_days: z.number(),
  analytics_hot_months: z.number(),
  restore_default_days: z.number(),
  min_ready_bytes: z.number(),
  sim_logs_max_age_days: z.number(),
  sim_logs_max_bytes: z.number(),
  alert_sync_lag_minutes: z.number(),
  alert_free_disk_gb: z.number(),
  alert_prod_live_multiple: z.number(),
  notify_digest_hour_utc: z.number(),
});
export type StorageSettings = z.infer<typeof StorageSettings>;

export const ActionModes = z.record(z.enum(["notify_only", "auto"]));

export const StorageSettingsResponse = z.object({ settings: StorageSettings, action_modes: ActionModes });

export const FloorRow = z.object({
  market_id: z.string(),
  window_seconds: z.number(),
  ticks_seconds: z.number(),
  checks_seconds: z.number(),
  overrides_seconds: z.number(),
  candles_seconds: z.number(),
  kronos_seconds: z.number(),
  requirement_seconds: z.number(),
  ceiling_seconds: z.number(),
  within_ceiling: z.boolean(),
  held_ticks_from: z.string().nullable(),
  held_candles_from: z.string().nullable(),
  held_kronos_from: z.string().nullable(),
});
export type FloorRow = z.infer<typeof FloorRow>;

export const EligibilityItem = z.object({
  action: z.string(),
  eligible: z.boolean(),
  ready_rows: z.number(),
  ready_bytes_estimate: z.number(),
  summary: z.string(),
  details: z.record(z.unknown()),
});
export type EligibilityItem = z.infer<typeof EligibilityItem>;

export const StorageAlert = z.object({
  key: z.string(),
  level: z.enum(["info", "warning"]),
  title: z.string(),
  message: z.string(),
  first_seen_at: z.string(),
  last_notified_at: z.string().nullable(),
  pending: z.boolean(),
});

export const StorageJob = z.object({
  job_id: z.string(),
  action: z.string(),
  status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  dry_run: z.boolean(),
  params: z.record(z.unknown()),
  progress: z.number().nullable(),
  message: z.string().nullable(),
  result: z.record(z.unknown()).nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});
export type StorageJob = z.infer<typeof StorageJob>;

/** Per-relation size + bloat (PostgreSQL `pg_stat_user_tables`). */
export const DbTableStats = z.object({
  name: z.string(),
  bytes: z.number(),
  live_rows: z.number(),
  dead_rows: z.number(),
  dead_ratio: z.number(),
});
export type DbTableStats = z.infer<typeof DbTableStats>;

const DbStats = z.object({
  path: z.string(),
  bytes: z.number().optional(),
  tables: z.array(DbTableStats).optional(),
});

export const StorageStatus = z.object({
  enabled: z.boolean(),
  deletion_blocked_reason: z.string().nullable(),
  settings: StorageSettings,
  action_modes: ActionModes,
  actions: z.array(z.string()),
  last_scan_at: z.string().nullable(),
  floors: z.array(FloorRow),
  eligibility: z.array(EligibilityItem),
  current_job: StorageJob.nullable(),
  alerts: z.array(StorageAlert),
  pending_alerts: z.number(),
  live_db: DbStats,
  analytics_db: DbStats.nullable(),
  disk_free_bytes: z.number().nullable(),
  archive_dir: z.string(),
});
export type StorageStatus = z.infer<typeof StorageStatus>;

export const ArchiveSegment = z.object({
  segment_id: z.string(),
  market_id: z.string(),
  month: z.string(),
  path: z.string(),
  sha256: z.string().nullable(),
  row_counts_json: z.string(),
  exported_at: z.string(),
  status: z.enum(["exported", "restored"]),
  restored_until: z.string().nullable(),
});
export type ArchiveSegment = z.infer<typeof ArchiveSegment>;

export const ActivationPlan = z.object({
  market_id: z.string(),
  config_version_id: z.string(),
  kronos_enabled: z.boolean(),
  requirement_seconds: z.number(),
  requirement_human: z.string().nullable(),
  ceiling_seconds: z.number(),
  within_ceiling: z.boolean(),
  uses_candles: z.boolean(),
  signature: z.string().nullable(),
  backfill: z.record(z.unknown()).nullable(),
});
export type ActivationPlan = z.infer<typeof ActivationPlan>;
